/**
 * Compass BD Intelligence Dashboard
 * 
 * Focused tool for finding team members and identifying gaps.
 * - Search for associates by experience, client, region
 * - Client Intelligence: who we've worked with
 * - Prospect Intelligence: where we have experience but haven't won work
 * - CV Gaps: associates missing CVs
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, CircularProgress, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, TextField, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Place as PlaceIcon,
  Send as SendIcon,
  CheckCircle as VerifiedIcon,
  RadioButtonUnchecked as UnverifiedIcon,
  Explore as ExploreIcon,
  Warning as WarningIcon,
  HelpOutline as HelpIcon,
  Groups as GroupsIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface OverviewStats {
  counts: {
    total_associates: number;
    total_cvs: number;
    cvs_extracted: number;
    associates_with_engagements: number;
    total_engagements: number;
    organisations: number;
    countries_covered: number;
    iod_parc_engagements: number;
    iod_parc_associates: number;
  };
  teamLeaders: number;
  recentEngagements: number;
  topSectors: Array<{ sector: string; associates: number }>;
  topRegions: Array<{ region: string; associates: number }>;
}

interface Opportunity {
  client_name: string;
  iod_projects: number;
  associates: number;
  cv_mentions: number;
  team_leaders: number;
  last_engagement_year: number;
  client_status: string;
}

interface ClientAssociates {
  client: {
    canonical_name: string;
    verified_project_count: number;
  };
  associates: Array<{
    associate_id: number;
    associate_name: string;
    project_count: number;
    sample_projects: string;
    primary_role: string;
  }>;
}

interface SearchResult {
  id: number;
  name: string;
  engagement_count: number;
  tl_count: number;
  most_recent_year: number;
  clients?: string;
  verified_project_count?: number;
  verified_client_count?: number;
}

interface AssociateProfile {
  associate: {
    id: number;
    canonical_name: string;
    clean_name: string;
    email?: string;
    languages?: string;
    nationality?: string;
  };
  stats: {
    total_engagements: number;
    tl_count: number;
    iod_parc_count: number;
    unique_clients: number;
    unique_countries: number;
    first_year: number;
    last_year: number;
    verified_project_count: number;
    verified_client_count: number;
    verified_engagements: number;
  };
  topClients: Array<{ id: number; canonical_name: string; engagement_count: number }>;
  topCountries: Array<{ name: string; region: string; engagement_count: number }>;
  engagements: Array<{
    id: number;
    programme_name: string;
    engagement_category: string;
    role_category: string;
    role_detail: string;
    year_start: number;
    year_end: number;
    contracting_org: string;
    end_client: string;
    primary_country: string;
    description: string;
    is_iod_parc_engagement: boolean;
    is_verified: boolean;
    iod_parc_project_number: string | null;
    countries: Array<{ name: string; region: string }>;
    sectors: Array<{ name: string }>;
  }>;
  verifiedProjects?: Array<{
    project_number: string;
    project_name: string;
    client_name: string;
    role: string;
    status: string;
  }>;
  rateCard?: {
    fee_per_day: number;
    rca_rate: number;
    currency: string;
    project_number: string;
    project_name: string;
  } | null;
}

// Shortlist interface
interface ShortlistMember {
  id: number;
  name: string;
  role: string;
  verified_project_count: number;
  engagement_count: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CompassDashboardPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchInterpretation, setSearchInterpretation] = useState('');

  // Profile dialog state
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AssociateProfile | null>(null);

  // Client profile dialog
  const [clientProfileOpen, setClientProfileOpen] = useState(false);
  const [clientProfile, setClientProfile] = useState<any>(null);
  const [clientProfileLoading, setClientProfileLoading] = useState(false);

  // Missing CVs state
  const [missingCVs, setMissingCVs] = useState<any>(null);

  // Client associates popup state
  const [clientAssociatesOpen, setClientAssociatesOpen] = useState(false);
  const [clientAssociates, setClientAssociates] = useState<ClientAssociates | null>(null);
  const [clientAssociatesLoading, setClientAssociatesLoading] = useState(false);

  // Prospects state (orgs where associates have experience but IOD hasn't worked)
  const [prospects, setProspects] = useState<any[]>([]);
  const [prospectAssociatesOpen, setProspectAssociatesOpen] = useState(false);
  const [prospectAssociates, setProspectAssociates] = useState<any>(null);
  const [prospectAssociatesLoading, setProspectAssociatesLoading] = useState(false);

  // Shortlist state
  const [shortlist, setShortlist] = useState<ShortlistMember[]>([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [pendingShortlistAdd, setPendingShortlistAdd] = useState<SearchResult | null>(null);
  const [selectedRole, setSelectedRole] = useState('');

  const ROLE_OPTIONS = ['Team Leader', 'Deputy Team Leader', 'Technical Lead', 'Specialist', 'Analyst', 'Researcher', 'M&E Expert', 'Gender Expert', 'Country Expert', 'Other'];

  const handleProspectClick = async (orgName: string) => {
    setProspectAssociatesLoading(true);
    setProspectAssociatesOpen(true);
    try {
      const res = await axios.get(`${API_BASE}/compass/stats?view=prospect-associates&orgName=${encodeURIComponent(orgName)}`);
      setProspectAssociates(res.data);
    } catch (err) {
      console.error('Failed to load prospect associates:', err);
      setProspectAssociates(null);
    } finally {
      setProspectAssociatesLoading(false);
    }
  };

  const handleClientClick = async (clientName: string) => {
    setClientAssociatesLoading(true);
    setClientAssociatesOpen(true);
    try {
      const res = await axios.get(`${API_BASE}/compass/stats?view=client-associates&clientName=${encodeURIComponent(clientName)}`);
      setClientAssociates(res.data);
    } catch (err) {
      console.error('Failed to load client associates:', err);
      setClientAssociates(null);
    } finally {
      setClientAssociatesLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch core data
      const [overviewRes, opportunitiesRes] = await Promise.all([
        axios.get(`${API_BASE}/compass/stats`),
        axios.get(`${API_BASE}/compass/stats?view=opportunities`)
      ]);
      setOverview(overviewRes.data);
      setOpportunities(opportunitiesRes.data.opportunities || []);
      
      // Fetch missing CVs separately - don't fail if this errors
      try {
        const missingCVsRes = await axios.get(`${API_BASE}/compass/stats?view=missing-cvs`);
        setMissingCVs(missingCVsRes.data);
      } catch (cvErr) {
        console.warn('Failed to load missing CVs:', cvErr);
        setMissingCVs({ summary: { missing_associates: 0, projects_affected: 0 }, associates: [] });
      }
      
      // Fetch prospects separately
      try {
        const prospectsRes = await axios.get(`${API_BASE}/compass/stats?view=prospects`);
        setProspects(prospectsRes.data.prospects || []);
      } catch (prospectErr) {
        console.warn('Failed to load prospects:', prospectErr);
        setProspects([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // AI-powered search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const response = await axios.post(`${API_BASE}/compass/search`, { query: searchQuery });
      // Sort results: verified projects first (descending by count), then by engagement count
      const sortedResults = (response.data.results || []).sort((a: SearchResult, b: SearchResult) => {
        const aVerified = a.verified_project_count || 0;
        const bVerified = b.verified_project_count || 0;
        if (bVerified !== aVerified) return bVerified - aVerified;
        return b.engagement_count - a.engagement_count;
      });
      setSearchResults(sortedResults);
      setSearchInterpretation(response.data.interpretation || '');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  // Load associate profile
  const openProfile = async (associateId: number) => {
    setProfileOpen(true);
    setProfileLoading(true);
    setSelectedProfile(null);
    try {
      const response = await axios.post(`${API_BASE}/compass/search`, { associateId });
      setSelectedProfile(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Load client profile
  const openClientProfile = async (clientId: number) => {
    setClientProfileOpen(true);
    setClientProfileLoading(true);
    setClientProfile(null);
    try {
      const response = await axios.post(`${API_BASE}/compass/search`, { organisationId: clientId });
      setClientProfile(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load client profile');
    } finally {
      setClientProfileLoading(false);
    }
  };

  // Shortlist handlers
  const handleAddToShortlist = (result: SearchResult) => {
    if (shortlist.some(m => m.id === result.id)) return; // Already in shortlist
    setPendingShortlistAdd(result);
    setSelectedRole('');
    setRoleDialogOpen(true);
  };

  const confirmAddToShortlist = () => {
    if (!pendingShortlistAdd || !selectedRole) return;
    setShortlist([...shortlist, {
      id: pendingShortlistAdd.id,
      name: pendingShortlistAdd.name,
      role: selectedRole,
      verified_project_count: pendingShortlistAdd.verified_project_count || 0,
      engagement_count: pendingShortlistAdd.engagement_count
    }]);
    setRoleDialogOpen(false);
    setPendingShortlistAdd(null);
    setSelectedRole('');
  };

  const removeFromShortlist = (id: number) => {
    setShortlist(shortlist.filter(m => m.id !== id));
  };

  const copyShortlistToClipboard = () => {
    const text = shortlist.map(m => `${m.role}: ${m.name}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const clearShortlist = () => {
    setShortlist([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Compass</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData} variant="outlined">
          Refresh
        </Button>
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<SearchIcon />} iconPosition="start" label="Find Evidence" />
          <Tab icon={<ExploreIcon />} iconPosition="start" label="Client Intelligence" />
          <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Prospect Intelligence" />
          <Tab icon={<WarningIcon />} iconPosition="start" label="CV Gaps" sx={{ color: tabValue === 3 ? '#ff9800' : 'inherit' }} />
        </Tabs>
      </Paper>

      {/* Find Evidence Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Find Evidence for Your Bid
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search by client, country, sector, topic, or role. AI will expand your search to find related terms.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="e.g. Who has worked with UNICEF in East Africa? / Find team leaders with health experience..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} disabled={searchLoading}>
                      {searchLoading ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Search Results */}
          {searchInterpretation && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              {searchInterpretation}
            </Typography>
          )}
          {searchInterpretation && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No associates found matching your search. Try different terms or check spelling.
            </Alert>
          )}
          {searchResults.length > 0 && (
            <TableContainer sx={{ mt: 2, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Associate</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        Verified
                        <Tooltip title="Verified = has worked on IOD PARC projects confirmed in Cappture. Unverified = CV experience only." arrow>
                          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        Work History
                        <Tooltip title="Number of projects/roles listed in their CV" arrow>
                          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">As TL</TableCell>
                    <TableCell align="right">Most Recent</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {r.verified_project_count && r.verified_project_count > 0 ? (
                            <Tooltip title="Has verified IOD PARC projects" arrow>
                              <VerifiedIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="CV experience only - no verified IOD projects yet" arrow>
                              <UnverifiedIcon sx={{ color: '#bdbdbd', fontSize: 18 }} />
                            </Tooltip>
                          )}
                          <Typography sx={{ fontWeight: 'bold' }}>{r.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {r.verified_project_count && r.verified_project_count > 0 ? (
                          <Chip 
                            size="small" 
                            label={`${r.verified_project_count} projects`}
                            sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#2e7d32', fontWeight: 'bold' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{r.engagement_count}</TableCell>
                      <TableCell align="right">{r.tl_count}</TableCell>
                      <TableCell align="right">{r.most_recent_year}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" onClick={() => openProfile(r.id)}>
                            View
                          </Button>
                          {shortlist.some(m => m.id === r.id) ? (
                            <Chip label="In Shortlist" size="small" color="success" />
                          ) : (
                            <Button 
                              size="small" 
                              variant="outlined" 
                              startIcon={<AddIcon />}
                              onClick={() => handleAddToShortlist(r)}
                            >
                              Shortlist
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Shortlist Panel */}
          {shortlist.length > 0 && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', border: '1px solid rgba(25, 118, 210, 0.2)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupsIcon />
                  Your Shortlist ({shortlist.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<CopyIcon />} onClick={copyShortlistToClipboard}>
                    Copy
                  </Button>
                  <Button size="small" color="error" onClick={clearShortlist}>
                    Clear
                  </Button>
                </Box>
              </Box>
              <Table size="small">
                <TableBody>
                  {shortlist.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell sx={{ width: 150 }}>
                        <Chip label={member.role} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 'bold' }}>{member.name}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {member.verified_project_count > 0 && (
                          <Chip 
                            label={`${member.verified_project_count} verified`} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#2e7d32' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => removeFromShortlist(member.id)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Paper>
      </TabPanel>

      {/* Role Selection Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Role for {pendingShortlistAdd?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {ROLE_OPTIONS.map((role) => (
              <Chip
                key={role}
                label={role}
                onClick={() => setSelectedRole(role)}
                color={selectedRole === role ? 'primary' : 'default'}
                variant={selectedRole === role ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmAddToShortlist} variant="contained" disabled={!selectedRole}>
            Add to Shortlist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Intelligence Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <ExploreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Client Intelligence
          </Typography>
          <Alert severity="success" sx={{ mb: 2 }}>
            All IOD clients from Cappture (source of truth). Click a row to see associates with experience.
          </Alert>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                  <TableCell>Client (from Cappture)</TableCell>
                  <TableCell align="right">IOD Projects</TableCell>
                  <TableCell align="right">CV Experience</TableCell>
                  <TableCell align="right">Team Leaders</TableCell>
                  <TableCell align="right">Last Engaged</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {opportunities.map((opp, idx) => (
                  <TableRow 
                    key={idx} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleClientClick(opp.client_name)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VerifiedIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 'bold' }}>{opp.client_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {opp.iod_projects}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{opp.associates || 0} associates</TableCell>
                    <TableCell align="right">{opp.team_leaders || 0}</TableCell>
                    <TableCell align="right">{opp.last_engagement_year || '-'}</TableCell>
                  </TableRow>
                ))}
                {opportunities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No clients found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            All clients from Cappture (source of truth), sorted by project count. CV Experience shows associates with matching CV entries.
          </Typography>
        </Paper>
      </TabPanel>

      {/* Prospect Intelligence Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Prospect Intelligence
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Organisations where our associates have experience, but IOD hasn't won work yet. Click a row to see who has worked there.
          </Alert>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Associates</TableCell>
                  <TableCell align="right">Team Leaders</TableCell>
                  <TableCell align="right">Engagements</TableCell>
                  <TableCell align="right">Most Recent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prospects.map((p, idx) => (
                  <TableRow 
                    key={idx} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleProspectClick(p.org_name)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UnverifiedIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 'bold' }}>{p.org_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={p.org_type || 'unknown'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                        {p.associates_with_experience}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{p.team_leaders || 0}</TableCell>
                    <TableCell align="right">{p.total_engagements}</TableCell>
                    <TableCell align="right">{p.most_recent_year || '-'}</TableCell>
                  </TableRow>
                ))}
                {prospects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No prospects found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Organisations where associates have CV experience but IOD hasn't won projects. Sorted by number of associates with experience.
          </Typography>
        </Paper>
      </TabPanel>

      {/* CV Gaps Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#ff9800' }} />
            Associates Missing CVs
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These associates have worked on IOD PARC projects (from Cappture) but don't have CVs in our system.
            <strong> We cannot evidence their experience without their CVs.</strong>
          </Alert>
          
          {missingCVs?.summary && (
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  {missingCVs.summary.missing_associates}
                </Typography>
                <Typography variant="body2" color="text.secondary">Associates Missing CVs</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                  {missingCVs.summary.projects_affected}
                </Typography>
                <Typography variant="body2" color="text.secondary">Projects Affected</Typography>
              </Box>
            </Box>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                  <TableCell>Associate Name</TableCell>
                  <TableCell align="right">Projects</TableCell>
                  <TableCell>Clients Worked With</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingCVs?.associates?.map((a: any, i: number) => (
                  <TableRow key={i} hover sx={{ bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UnverifiedIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 'bold' }}>{a.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${a.project_count} projects`} 
                        size="small" 
                        sx={{ bgcolor: a.project_count >= 5 ? '#ffebee' : 'default', color: a.project_count >= 5 ? '#c62828' : 'inherit' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.sample_client || a.clients || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" color="warning">
                        Request CV
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Associate Profile Dialog */}
      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              {selectedProfile?.associate.clean_name || 'Loading...'}
            </Box>
            <IconButton onClick={() => setProfileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedProfile ? (
            <Grid container spacing={3}>
              {/* Stats Summary */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {selectedProfile.stats.verified_project_count > 0 && (
                    <Chip 
                      icon={<VerifiedIcon />} 
                      label={`${selectedProfile.stats.verified_project_count} verified projects`} 
                      sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#2e7d32', fontWeight: 'bold' }}
                    />
                  )}
                  <Chip icon={<WorkIcon />} label={`${selectedProfile.stats.total_engagements} work history entries`} color="primary" />
                  <Chip icon={<WorkIcon />} label={`${selectedProfile.stats.tl_count} as Team Leader`} color="success" />
                  <Chip icon={<BusinessIcon />} label={`${selectedProfile.stats.unique_clients} clients`} />
                  <Chip icon={<PlaceIcon />} label={`${selectedProfile.stats.unique_countries} countries`} />
                  <Chip label={`${selectedProfile.stats.first_year} - ${selectedProfile.stats.last_year}`} variant="outlined" />
                </Box>
              </Grid>

              {/* Rate Card - only show if we have rate data */}
              {selectedProfile.rateCard && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                      💰 Rate Card (from most recent project)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Fee Rate</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {selectedProfile.rateCard.currency} {selectedProfile.rateCard.fee_per_day?.toLocaleString()}/day
                        </Typography>
                      </Box>
                      {selectedProfile.rateCard.rca_rate && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">RCA Rate</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {selectedProfile.rateCard.currency} {selectedProfile.rateCard.rca_rate?.toLocaleString()}/day
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          From: {selectedProfile.rateCard.project_number}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Top Clients */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Top Clients</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedProfile.topClients.slice(0, 8).map((c, i) => (
                    <Chip key={i} label={`${c.canonical_name} (${c.engagement_count})`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>

              {/* Top Countries */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Top Countries</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedProfile.topCountries.slice(0, 8).map((c, i) => (
                    <Chip key={i} label={`${c.name} (${c.engagement_count})`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>

              {/* Verified Projects from Cappture */}
              {selectedProfile.verifiedProjects && selectedProfile.verifiedProjects.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedIcon sx={{ color: '#4caf50' }} />
                    Verified Projects ({selectedProfile.verifiedProjects.length})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                          <TableCell>Project</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProfile.verifiedProjects.map((proj, idx) => (
                          <TableRow key={idx} hover sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {proj.project_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {proj.project_name}
                              </Typography>
                            </TableCell>
                            <TableCell>{proj.client_name}</TableCell>
                            <TableCell>{proj.role || '-'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={proj.status} 
                                size="small" 
                                color={proj.status?.includes('Active') ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              {/* CV Engagements List */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  Work History from CV ({selectedProfile.engagements.length})
                  <Tooltip title="Projects and roles extracted from their CV. Verified entries (green) are confirmed IOD PARC projects." arrow>
                    <HelpIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Programme</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>Sectors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProfile.engagements.map((eng) => (
                        <TableRow key={eng.id} hover sx={eng.is_verified ? { bgcolor: 'rgba(76, 175, 80, 0.1)' } : {}}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {eng.is_verified ? (
                                <VerifiedIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                              ) : (
                                <UnverifiedIcon sx={{ color: '#bdbdbd', fontSize: 16 }} />
                              )}
                              {eng.year_start}{eng.year_end && eng.year_end !== eng.year_start ? `-${eng.year_end}` : ''}
                              {eng.iod_parc_project_number && (
                                <Chip 
                                  label={eng.iod_parc_project_number} 
                                  size="small" 
                                  sx={{ ml: 1, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ minWidth: 300 }}>
                            <Typography variant="body2" title={eng.programme_name}>
                              {eng.programme_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{eng.end_client || eng.contracting_org || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={eng.role_category?.replace('_', ' ') || '-'} 
                              size="small"
                              color={eng.role_category === 'team_leader' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{eng.primary_country || eng.countries?.[0]?.name || '-'}</TableCell>
                          <TableCell>
                            {eng.sectors?.slice(0, 2).map((s, i) => (
                              <Chip key={i} label={s.name} size="small" sx={{ mr: 0.5 }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="error">Failed to load profile</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Client Profile Dialog */}
      <Dialog open={clientProfileOpen} onClose={() => setClientProfileOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              {clientProfile?.organisation?.canonical_name || 'Loading...'}
              {clientProfile?.organisation?.is_iod_client && (
                <Chip 
                  icon={<VerifiedIcon />} 
                  label="IOD Client" 
                  size="small" 
                  sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#2e7d32' }}
                />
              )}
            </Box>
            <IconButton onClick={() => setClientProfileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {clientProfileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : clientProfile ? (
            <Grid container spacing={3}>
              {/* Track Record Summary */}
              <Grid item xs={12}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Our Track Record with {clientProfile.organisation?.canonical_name}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {clientProfile.stats?.total_engagements || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">CV Engagements</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                        {clientProfile.stats?.unique_associates || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Associates</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                        {clientProfile.stats?.team_leaders || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Team Leaders</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                        {clientProfile.stats?.first_year} - {clientProfile.stats?.last_year}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Years Active</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Top Sectors */}
              {clientProfile.topSectors?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Sectors We've Covered</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {clientProfile.topSectors.map((s: any, i: number) => (
                      <Chip key={i} label={`${s.name} (${s.count})`} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Top Countries */}
              {clientProfile.topCountries?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Countries We've Covered</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {clientProfile.topCountries.map((c: any, i: number) => (
                      <Chip key={i} label={`${c.name} (${c.count})`} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Associates with Experience */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Associates with {clientProfile.organisation?.canonical_name} Experience
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Associate</TableCell>
                        <TableCell align="right">Engagements</TableCell>
                        <TableCell align="right">As TL</TableCell>
                        <TableCell align="right">Most Recent</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientProfile.associates?.map((a: any) => (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ fontWeight: 'bold' }}>{a.name}</TableCell>
                          <TableCell align="right">{a.engagement_count}</TableCell>
                          <TableCell align="right">{a.tl_count}</TableCell>
                          <TableCell align="right">{a.most_recent_year}</TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => { setClientProfileOpen(false); openProfile(a.id); }}>
                              View Profile
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="error">Failed to load client profile</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Client Associates Popup */}
      <Dialog open={clientAssociatesOpen} onClose={() => setClientAssociatesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {clientAssociates?.client?.verified_project_count ? (
                <VerifiedIcon sx={{ color: '#4caf50' }} />
              ) : (
                <UnverifiedIcon sx={{ color: '#ff9800' }} />
              )}
              <Typography variant="h6">{clientAssociates?.client?.canonical_name || 'Client'}</Typography>
              <Chip 
                label={clientAssociates?.client?.verified_project_count ? 
                  `${clientAssociates.client.verified_project_count} IOD Projects` : 
                  'Prospect'
                } 
                size="small" 
                color={clientAssociates?.client?.verified_project_count ? 'success' : 'warning'}
              />
            </Box>
            <IconButton onClick={() => setClientAssociatesOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {clientAssociatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : clientAssociates ? (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Associates allocated to projects for this client (from Cappture)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                      <TableCell>Associate</TableCell>
                      <TableCell align="right">Projects</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientAssociates.associates?.map((a, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 'bold' }}>{a.associate_name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.sample_projects}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={a.project_count} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{a.primary_role}</Typography>
                        </TableCell>
                        <TableCell>
                          {a.associate_id > 0 && (
                            <Button 
                              size="small" 
                              onClick={() => { setClientAssociatesOpen(false); openProfile(a.associate_id); }}
                            >
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!clientAssociates.associates || clientAssociates.associates.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">No associates allocated to projects for this client</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="error">Failed to load client associates</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientAssociatesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Prospect Associates Popup */}
      <Dialog open={prospectAssociatesOpen} onClose={() => setProspectAssociatesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: '#2196f3' }} />
              <Typography variant="h6">{prospectAssociates?.organisation?.canonical_name || 'Organisation'}</Typography>
              <Chip label="Prospect" size="small" color="info" />
            </Box>
            <IconButton onClick={() => setProspectAssociatesOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {prospectAssociatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : prospectAssociates ? (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Associates with experience at this organisation (from CVs)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                      <TableCell>Associate</TableCell>
                      <TableCell align="right">Engagements</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Most Recent</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prospectAssociates.associates?.map((a: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 'bold' }}>{a.associate_name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.sample_programmes}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={a.engagement_count} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{a.primary_role}</Typography>
                        </TableCell>
                        <TableCell align="right">{a.most_recent_year || '-'}</TableCell>
                        <TableCell>
                          {a.associate_id > 0 && (
                            <Button 
                              size="small" 
                              onClick={() => { setProspectAssociatesOpen(false); openProfile(a.associate_id); }}
                            >
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!prospectAssociates.associates || prospectAssociates.associates.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">No associates found with experience at this organisation</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="error">Failed to load prospect associates</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProspectAssociatesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompassDashboardPage;
